import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { RefreshCw, UserX, CheckCircle2, ShieldCheck, FileSignature, Download } from "lucide-react";
import { API } from "@/lib/api";

const TYPES = [
  { value: "resignation", label: "Resignation" },
  { value: "performance_based", label: "Performance-based" },
  { value: "disciplinary", label: "Disciplinary" },
];

/** Phase 5 — Termination & Clearance desktop (PM/Admin/Sales Head). */
export default function TerminationManagement() {
  const { can, user } = useAuth();
  const canInitiate = can("promoter_termination.initiate");
  const canPMSign = can("promoter_termination.pm_signoff");
  const canAdminSign = can("promoter_termination.admin_signoff");

  const [rows, setRows] = useState([]);
  const [promoters, setPromoters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("__all__");
  const [initiating, setInitiating] = useState(false);
  const [active, setActive] = useState(null);  // detail dialog

  const load = async () => {
    setLoading(true);
    try {
      const params = statusFilter === "__all__" ? {} : { status: statusFilter };
      const { data } = await api.get("/promoter/terminations", { params });
      setRows(data || []);
    } catch (e) {
      toast.error("Could not load", { description: formatApiErrorDetail(e.response?.data?.detail) });
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter]);
  useEffect(() => {
    api.get("/promoter/promoters", { params: { active: true } })
      .then(({ data }) => setPromoters(data || []))
      .catch(() => {});
  }, []);

  const exportLedger = () => {
    const token = localStorage.getItem("access_token");
    const params = new URLSearchParams();
    if (statusFilter !== "__all__") params.append("status", statusFilter);
    fetch(`${API}/promoter/exports/termination-ledger.xlsx?${params.toString()}`,
          { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => { if (!r.ok) throw new Error("export"); return r.blob(); })
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a"); a.href = url; a.download = "termination_ledger.xlsx"; a.click();
        URL.revokeObjectURL(url);
      })
      .catch(() => toast.error("Could not export"));
  };

  return (
    <div className="space-y-4" data-testid="termination-page">
      <header className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="font-heading text-3xl font-bold text-slate-900">Termination &amp; Clearance</h1>
          <p className="text-sm text-slate-500 mt-1">
            Dual sign-off workflow. Admin final approval deactivates the promoter login while preserving all historical records.
          </p>
        </div>
        <div className="flex gap-2 items-end">
          <div>
            <Label className="text-[11px] uppercase font-semibold text-slate-600">Status</Label>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="h-8 w-48" data-testid="termination-status-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All</SelectItem>
                <SelectItem value="initiated">Initiated</SelectItem>
                <SelectItem value="pm_signed_off">PM signed off</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button variant="outline" onClick={load} data-testid="termination-refresh">
            <RefreshCw size={14} className="mr-1" /> Refresh
          </Button>
          <Button variant="outline" onClick={exportLedger} data-testid="termination-export-btn">
            <Download size={14} className="mr-1" /> Export
          </Button>
          {canInitiate && (
            <Button onClick={() => setInitiating(true)}
                    className="bg-slate-900 hover:bg-slate-800 text-white"
                    data-testid="termination-initiate-btn">
              <UserX size={14} className="mr-1" /> Initiate
            </Button>
          )}
        </div>
      </header>

      <Card><CardContent className="p-0">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="termination-table">
            <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wide">
              <tr>
                <th className="text-left px-3 py-2">Initiated</th>
                <th className="text-left px-3 py-2">Promoter</th>
                <th className="text-left px-3 py-2">Type</th>
                <th className="text-left px-3 py-2">Proposed LWD</th>
                <th className="text-right px-3 py-2">Final Settlement</th>
                <th className="text-left px-3 py-2">Status</th>
                <th className="text-right px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={7} className="text-center py-6 text-slate-400">Loading…</td></tr>}
              {!loading && rows.length === 0 && (
                <tr><td colSpan={7} className="text-center py-6 text-slate-400" data-testid="termination-empty">
                  No termination records in this filter.
                </td></tr>
              )}
              {!loading && rows.map((r) => (
                <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50/60"
                    data-testid={`termination-row-${r.id.slice(0, 8)}`}>
                  <td className="px-3 py-2 text-xs font-mono">{(r.initiated_at || "").slice(0, 10)}</td>
                  <td className="px-3 py-2">{r.promoter_name}<div className="text-[10px] text-slate-400">{r.promoter_promoter_id}</div></td>
                  <td className="px-3 py-2 text-xs">{TYPES.find((t) => t.value === r.termination_type)?.label || r.termination_type}</td>
                  <td className="px-3 py-2 text-xs">{r.proposed_last_working_date || "—"}</td>
                  <td className="px-3 py-2 text-right font-semibold">Rs. {Number(r.final_settlement_amount || 0).toLocaleString()}</td>
                  <td className="px-3 py-2"><StatusPill status={r.status} /></td>
                  <td className="px-3 py-2 text-right">
                    <Button size="sm" variant="outline" onClick={() => setActive(r)}
                            data-testid={`termination-view-${r.id.slice(0, 8)}`}>
                      Open
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent></Card>

      <InitiateDialog open={initiating} promoters={promoters} onClose={() => setInitiating(false)} onDone={() => { setInitiating(false); load(); }} />
      <DetailDialog state={active} onClose={() => setActive(null)} onDone={() => { setActive(null); load(); }}
                    canPMSign={canPMSign} canAdminSign={canAdminSign} currentUserId={user?.id} />
    </div>
  );
}

function StatusPill({ status }) {
  const m = {
    initiated: "bg-amber-100 text-amber-800",
    pm_signed_off: "bg-sky-100 text-sky-800",
    completed: "bg-emerald-100 text-emerald-800",
    cancelled: "bg-slate-200 text-slate-700",
  }[status] || "bg-slate-100 text-slate-700";
  return <Badge className={m}>{(status || "").replace(/_/g, " ")}</Badge>;
}

function InitiateDialog({ open, promoters, onClose, onDone }) {
  const [form, setForm] = useState({ promoter_id: "", termination_type: "", reason: "", proposed_last_working_date: "", remarks: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setForm({ promoter_id: "", termination_type: "", reason: "", proposed_last_working_date: "", remarks: "" }); }, [open]);

  const submit = async () => {
    if (!form.promoter_id) { toast.error("Pick a promoter"); return; }
    if (!form.termination_type) { toast.error("Pick a termination type"); return; }
    if (!form.reason.trim()) { toast.error("Reason is required"); return; }
    if (!form.proposed_last_working_date) { toast.error("Proposed LWD is required"); return; }
    setBusy(true);
    try {
      await api.post("/promoter/terminations", form);
      toast.success("Termination initiated");
      onDone();
    } catch (e) {
      toast.error("Could not initiate", { description: formatApiErrorDetail(e.response?.data?.detail) });
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="termination-initiate-dialog">
        <DialogHeader>
          <DialogTitle>Initiate termination</DialogTitle>
          <DialogDescription>
            Captures the reason and proposed last working date. The Product Manager fills the clearance checklist later, then dual sign-off (PM + Admin) is required to deactivate the promoter login.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <Field label="Promoter" required>
            <Select value={form.promoter_id} onValueChange={(v) => setForm({ ...form, promoter_id: v })}>
              <SelectTrigger data-testid="init-promoter"><SelectValue placeholder="Select promoter" /></SelectTrigger>
              <SelectContent>
                {promoters.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} ({p.promoter_id})</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Termination type" required>
            <Select value={form.termination_type} onValueChange={(v) => setForm({ ...form, termination_type: v })}>
              <SelectTrigger data-testid="init-type"><SelectValue placeholder="Select type" /></SelectTrigger>
              <SelectContent>{TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Reason" required><Textarea rows={2} value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} data-testid="init-reason" /></Field>
          <Field label="Proposed last working date" required><Input type="date" value={form.proposed_last_working_date} onChange={(e) => setForm({ ...form, proposed_last_working_date: e.target.value })} data-testid="init-lwd" /></Field>
          <Field label="Remarks (optional)"><Textarea rows={2} value={form.remarks} onChange={(e) => setForm({ ...form, remarks: e.target.value })} data-testid="init-remarks" /></Field>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={busy} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="init-confirm">
            {busy ? "Saving…" : "Initiate"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DetailDialog({ state, onClose, onDone, canPMSign, canAdminSign, currentUserId }) {
  const [doc, setDoc] = useState(null);
  const [busy, setBusy] = useState(false);
  const [overrideReason, setOverrideReason] = useState("");

  useEffect(() => {
    if (!state) { setDoc(null); setOverrideReason(""); return; }
    api.get(`/promoter/terminations/${state.id}`)
      .then(({ data }) => {
        // Phase 5.1 — auto-prefill pending_approved_incentive with the live
        // suggestion the FIRST time the dialog opens (i.e. while still 0/null
        // and no value has been entered yet). PM remains free to edit.
        const suggested = Number(data.payable_incentive_suggested || 0);
        const current = Number(data.pending_approved_incentive || 0);
        if (current === 0 && suggested > 0 && data.status === "initiated") {
          setDoc({ ...data, pending_approved_incentive: suggested });
        } else {
          setDoc(data);
        }
        setOverrideReason(data.incentive_override_reason || "");
      })
      .catch((e) => toast.error("Could not load", { description: formatApiErrorDetail(e.response?.data?.detail) }));
  }, [state]);

  const status = doc?.status;
  const checklistComplete = !!(doc && doc.assets_returned === true && doc.dues_settled === true && doc.final_attendance_verified === true);
  const editable = status === "initiated" && canPMSign;
  const canPM = status === "initiated" && canPMSign;
  const canAdmin = status === "pm_signed_off" && canAdminSign && checklistComplete && doc?.pm_signed_off_by_id !== currentUserId;
  // Iter42 — explain to the user why Admin cannot final-approve right now
  let adminBlockedReason = null;
  if (status === "completed") adminBlockedReason = "Already completed";
  else if (!canAdminSign) adminBlockedReason = "You don't have permission to final-approve terminations";
  else if (status === "initiated") adminBlockedReason = "PM sign-off is still pending";
  else if (status === "pm_signed_off" && !checklistComplete) adminBlockedReason = "Clearance checklist is incomplete (assets / dues / attendance)";
  else if (status === "pm_signed_off" && doc?.pm_signed_off_by_id === currentUserId) adminBlockedReason = "You signed off as PM — another Admin must final-approve (4-eye rule)";
  const suggested = Number(doc?.payable_incentive_suggested || 0);
  const incentiveValue = Number(doc?.pending_approved_incentive || 0);
  const isOverride = Math.abs(incentiveValue - suggested) > 1;
  const overrideHistory = doc?.incentive_override_history || [];

  const finalSettlement = useMemo(() => {
    if (!doc) return 0;
    return (Number(doc.pending_salary || 0) + Number(doc.pending_approved_incentive || 0)) - Number(doc.deductions || 0);
  }, [doc]);

  const saveChecklist = async () => {
    if (!doc) return;
    if (isOverride && !overrideReason.trim()) {
      toast.error("Override reason required", { description: `Incentive differs from suggested Rs. ${suggested.toLocaleString()} — please add a reason.` });
      return;
    }
    setBusy(true);
    try {
      const payload = {
        assets_returned: doc.assets_returned ?? null, assets_remarks: doc.assets_remarks ?? null,
        dues_settled: doc.dues_settled ?? null, dues_remarks: doc.dues_remarks ?? null,
        final_attendance_verified: doc.final_attendance_verified ?? null, attendance_remarks: doc.attendance_remarks ?? null,
        pending_salary: Number(doc.pending_salary || 0),
        pending_approved_incentive: Number(doc.pending_approved_incentive || 0),
        deductions: Number(doc.deductions || 0),
        actual_last_working_date: doc.actual_last_working_date || null,
        settlement_remarks: doc.settlement_remarks ?? null,
        incentive_override_reason: isOverride ? overrideReason.trim() : null,
      };
      const { data } = await api.put(`/promoter/terminations/${doc.id}/checklist`, payload);
      setDoc(data);
      setOverrideReason(data.incentive_override_reason || "");
      toast.success("Checklist saved");
    } catch (e) {
      toast.error("Could not save", { description: formatApiErrorDetail(e.response?.data?.detail) });
    } finally { setBusy(false); }
  };

  const sign = async (kind) => {
    if (!doc) return;
    const remarks = window.prompt(`${kind === "pm" ? "PM" : "Admin"} sign-off remarks (optional):`, "") || "";
    setBusy(true);
    try {
      await api.post(`/promoter/terminations/${doc.id}/${kind}-signoff`, { remarks });
      toast.success(kind === "pm" ? "PM sign-off recorded" : "Admin sign-off recorded — login deactivated");
      onDone();
    } catch (e) {
      toast.error("Sign-off failed", { description: formatApiErrorDetail(e.response?.data?.detail) });
    } finally { setBusy(false); }
  };

  if (!state) return null;

  return (
    <Dialog open={!!state} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="termination-detail-dialog">
        <DialogHeader>
          <DialogTitle>
            {state.promoter_name} · {TYPES.find((t) => t.value === state.termination_type)?.label || state.termination_type}
          </DialogTitle>
          <DialogDescription>
            Clearance checklist, settlement amounts and dual sign-off. The pending approved incentive is auto-suggested from the live calculation (net-sales based) — overrides require a written reason that is added to the audit log.
          </DialogDescription>
        </DialogHeader>
        {!doc && <div className="text-sm text-slate-500">Loading…</div>}
        {doc && (
          <div className="space-y-4 text-sm">
            <div className="grid grid-cols-2 gap-2">
              <Row label="Promoter" value={`${doc.promoter_name} (${doc.promoter_promoter_id})`} />
              <Row label="Status" value={<StatusPill status={doc.status} />} />
              <Row label="Initiated" value={`${doc.initiated_by || "—"} · ${(doc.initiated_at || "").slice(0, 19)}`} />
              <Row label="Type" value={TYPES.find((t) => t.value === doc.termination_type)?.label || doc.termination_type} />
              <Row label="Reason" value={doc.reason} />
              <Row label="Proposed LWD" value={doc.proposed_last_working_date} />
            </div>

            <Card><CardContent className="p-3 space-y-3" data-testid="checklist-card">
              <div className="font-semibold text-slate-900">Clearance checklist</div>
              <CheckRow label="Assets returned" testId="chk-assets"
                        value={doc.assets_returned} editable={editable}
                        onChange={(v) => setDoc({ ...doc, assets_returned: v })}
                        remarks={doc.assets_remarks || ""} onRemarks={(v) => setDoc({ ...doc, assets_remarks: v })} />
              <CheckRow label="Outstanding dues settled" testId="chk-dues"
                        value={doc.dues_settled} editable={editable}
                        onChange={(v) => setDoc({ ...doc, dues_settled: v })}
                        remarks={doc.dues_remarks || ""} onRemarks={(v) => setDoc({ ...doc, dues_remarks: v })} />
              <CheckRow label="Final attendance verified" testId="chk-att"
                        value={doc.final_attendance_verified} editable={editable}
                        onChange={(v) => setDoc({ ...doc, final_attendance_verified: v })}
                        remarks={doc.attendance_remarks || ""} onRemarks={(v) => setDoc({ ...doc, attendance_remarks: v })} />

              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 pt-2 border-t border-slate-100">
                <Field label="Pending salary">
                  <Input type="number" value={doc.pending_salary ?? 0} disabled={!editable}
                         onChange={(e) => setDoc({ ...doc, pending_salary: Number(e.target.value || 0) })}
                         data-testid="amt-salary" />
                </Field>
                <Field label="Pending approved incentive">
                  <Input type="number" value={doc.pending_approved_incentive ?? 0} disabled={!editable}
                         onChange={(e) => setDoc({ ...doc, pending_approved_incentive: Number(e.target.value || 0) })}
                         data-testid="amt-incentive" />
                  <div className="text-[10px] text-slate-500 mt-0.5" data-testid="amt-incentive-suggested">
                    Auto-suggested (live calc, {doc.incentive_suggested_month || "current month"}):
                    Rs. {suggested.toLocaleString()}
                    {isOverride && status === "initiated" && (
                      <span className="text-amber-700 font-semibold ml-1">· override</span>
                    )}
                  </div>
                </Field>
                <Field label="Deductions">
                  <Input type="number" value={doc.deductions ?? 0} disabled={!editable}
                         onChange={(e) => setDoc({ ...doc, deductions: Number(e.target.value || 0) })}
                         data-testid="amt-deductions" />
                </Field>
                <Field label="Final settlement (auto)">
                  <Input value={`Rs. ${finalSettlement.toLocaleString()}`} disabled data-testid="amt-final" />
                </Field>
              </div>
              {editable && isOverride && (
                <Field label="Override reason (required)" required>
                  <Textarea rows={2} value={overrideReason}
                            onChange={(e) => setOverrideReason(e.target.value)}
                            data-testid="amt-override-reason"
                            placeholder="Explain why the incentive differs from the live calculation" />
                </Field>
              )}
              {overrideHistory.length > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded p-2 text-xs space-y-1"
                     data-testid="amt-override-history">
                  <div className="font-semibold text-amber-900">Incentive override audit ({overrideHistory.length})</div>
                  {overrideHistory.slice(-3).map((h, i) => (
                    <div key={i} className="text-amber-800">
                      <span className="font-mono">{(h.at || "").slice(0, 19)}</span> · {h.by_name || h.by_id} ·
                      suggested Rs. {Number(h.suggested).toLocaleString()} → set Rs. {Number(h.override_value).toLocaleString()}
                      {h.reason ? ` · “${h.reason}”` : ""}
                    </div>
                  ))}
                </div>
              )}
              <Field label="Settlement remarks">
                <Textarea rows={2} value={doc.settlement_remarks || ""} disabled={!editable}
                          onChange={(e) => setDoc({ ...doc, settlement_remarks: e.target.value })}
                          data-testid="amt-remarks" />
              </Field>
              <Field label="Actual last working date">
                <Input type="date" value={doc.actual_last_working_date || ""} disabled={!editable}
                       onChange={(e) => setDoc({ ...doc, actual_last_working_date: e.target.value })}
                       data-testid="amt-lwd" />
              </Field>
              {editable && (
                <div className="flex justify-end">
                  <Button onClick={saveChecklist} disabled={busy} variant="outline" data-testid="checklist-save-btn">
                    {busy ? "Saving…" : "Save checklist"}
                  </Button>
                </div>
              )}
            </CardContent></Card>

            {doc.pm_signed_off_at && (
              <div className="text-xs text-slate-600" data-testid="pm-signoff-info">
                <FileSignature size={12} className="inline mr-1" />
                PM sign-off by {doc.pm_signed_off_by} · {(doc.pm_signed_off_at || "").slice(0, 19)}
                {doc.pm_signoff_remarks ? ` · ${doc.pm_signoff_remarks}` : ""}
              </div>
            )}
            {doc.admin_signed_off_at && (
              <div className="text-xs text-emerald-700" data-testid="admin-signoff-info">
                <ShieldCheck size={12} className="inline mr-1" />
                Admin sign-off by {doc.admin_signed_off_by} · {(doc.admin_signed_off_at || "").slice(0, 19)} — login deactivated
                {doc.admin_signoff_remarks ? ` · ${doc.admin_signoff_remarks}` : ""}
              </div>
            )}

            <DialogFooter className="flex-col sm:flex-row gap-2">
              {canPM && (
                <Button onClick={() => sign("pm")} disabled={busy} className="bg-sky-700 hover:bg-sky-800 text-white" data-testid="pm-signoff-btn">
                  <FileSignature size={14} className="mr-1" /> PM sign-off
                </Button>
              )}
              {canAdmin && (
                <Button onClick={() => sign("admin")} disabled={busy} className="bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="admin-signoff-btn">
                  <ShieldCheck size={14} className="mr-1" /> Final Approve · Complete Clearance
                </Button>
              )}
              {!canPM && !canAdmin && status !== "completed" && adminBlockedReason && (
                <span className="text-xs text-amber-700 bg-amber-50 border border-amber-200 px-2 py-1 rounded"
                      data-testid="admin-signoff-blocked-reason">
                  Final approval unavailable: {adminBlockedReason}
                </span>
              )}
              {status === "completed" && (
                <Badge className="bg-emerald-100 text-emerald-800"><CheckCircle2 size={12} className="mr-1" /> Completed</Badge>
              )}
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function CheckRow({ label, value, editable, onChange, remarks, onRemarks, testId }) {
  return (
    <div className="grid grid-cols-3 gap-2 items-start" data-testid={testId}>
      <div>
        <div className="text-xs font-semibold text-slate-700">{label}</div>
        <Select value={value === true ? "yes" : value === false ? "no" : ""} disabled={!editable}
                onValueChange={(v) => onChange(v === "yes" ? true : v === "no" ? false : null)}>
          <SelectTrigger className="h-8 mt-1" data-testid={`${testId}-select`}><SelectValue placeholder="—" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="yes">Yes</SelectItem>
            <SelectItem value="no">No</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="col-span-2">
        <Label className="text-[11px] uppercase font-semibold text-slate-600">Remarks</Label>
        <Input value={remarks || ""} disabled={!editable} onChange={(e) => onRemarks(e.target.value)} data-testid={`${testId}-remarks`} />
      </div>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <div className="space-y-1">
      <Label className="text-[11px] uppercase font-semibold text-slate-600">{label}{required && <span className="text-rose-600 ml-1">*</span>}</Label>
      {children}
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className="text-sm text-slate-900 break-words">{typeof value === "string" || typeof value === "number" ? String(value) : value}</div>
    </div>
  );
}
